import React, { useEffect, useState, useMemo } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Loader2, Search, Filter, TrendingDown, Users, Package as PackageIcon } from 'lucide-react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { dealsAPI } from '../lib/api';
import ShareButton from '../components/ShareButton';

export default function DealDetailPage() {
  const { slug } = useParams();
  const [deal, setDeal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState('all');
  const [sort, setSort] = useState('featured');

  useEffect(() => {
    (async () => {
      try {
        const { data } = await dealsAPI.getBySlug(slug);
        setDeal(data);
      } catch (e) { console.error(e); }
      setLoading(false);
    })();
  }, [slug]);

  const categories = useMemo(() => {
    if (!deal?.products) return [];
    const set = new Set(deal.products.map(p => p.product?.category).filter(Boolean));
    return ['all', ...set];
  }, [deal]);

  const filtered = useMemo(() => {
    if (!deal?.products) return [];
    let arr = [...deal.products];
    if (category !== 'all') arr = arr.filter(dp => dp.product?.category === category);
    if (search) {
      const q = search.toLowerCase();
      arr = arr.filter(dp =>
        (dp.product?.title || '').toLowerCase().includes(q) ||
        (dp.product?.brand || '').toLowerCase().includes(q)
      );
    }
    arr.sort((a, b) => {
      if (sort === 'featured') return (b.featured ? 1 : 0) - (a.featured ? 1 : 0);
      if (sort === 'price') return (a.current_price || 0) - (b.current_price || 0);
      if (sort === 'popular') return (b.interested_count || 0) - (a.interested_count || 0);
      if (sort === 'unlocking') return (a.orders_to_next_tier ?? 999) - (b.orders_to_next_tier ?? 999);
      return 0;
    });
    return arr;
  }, [deal, search, category, sort]);

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex items-center justify-center pt-24">
          <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
        </div>
      </>
    );
  }

  if (!deal) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen flex flex-col items-center justify-center pt-24">
          <p className="text-slate-600 mb-4">Deal introuvable</p>
          <Link to="/deals"><Button>← Retour aux Deals</Button></Link>
        </div>
      </>
    );
  }

  return (
    <>
      <Navbar />
      <main className="min-h-screen bg-slate-50 pt-20 pb-16">
        {/* HERO */}
        <section className="bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 text-white py-12 mb-8">
          <div className="max-w-6xl mx-auto px-4">
            <Link to="/deals" className="text-amber-300 hover:text-amber-200 text-sm mb-4 inline-block">← Toutes les opérations</Link>
            <div className="flex items-start gap-6">
              <div className="text-7xl">{deal.destination?.flag || '🌍'}</div>
              <div className="flex-1">
                <div className="flex items-start justify-between gap-4 mb-3">
                  <Badge className="bg-amber-500 hover:bg-amber-600">{deal.destination?.name}</Badge>
                  <ShareButton
                    variant="button"
                    title={deal.name}
                    message={`🔥 Départ groupé ISM Parcel vers ${deal.destination?.name} — ${deal.products_count || 0} produits sélectionnés, plus on est nombreux, moins ça coûte. 👉 ${window.location.href}`}
                    className="bg-white/10 hover:bg-white/20 text-white border-white/20"
                    testId="deal-share-btn"
                  />
                </div>
                <h1 className="text-3xl sm:text-4xl lg:text-5xl font-black mb-3" style={{fontFamily: 'Chivo, sans-serif'}}>{deal.name}</h1>
                {deal.description && <p className="text-slate-300 max-w-2xl">{deal.description}</p>}
                <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mt-6">
                  <Stat label="Produits" value={deal.products_count} />
                  <Stat label="👀 Intéressés" value={deal.interests_count} />
                  <Stat label="🛒 Déclarées" value={deal.declared_orders_count} />
                  <Stat label="📦 Reçues" value={deal.received_orders_count} highlight />
                  <Stat label="🪵 Palettes" value={deal.pallets_count} />
                </div>
                {deal.close_at && (
                  <p className="mt-4 text-sm text-slate-400">
                    Date limite : <span className="text-white font-semibold">{new Date(deal.close_at).toLocaleDateString('fr-FR', { day:'numeric', month:'long', year:'numeric'})}</span>
                    {deal.estimated_departure && <> — Départ estimé : <span className="text-white font-semibold">{deal.estimated_departure}</span></>}
                  </p>
                )}
              </div>
            </div>
            <p className="mt-6 text-xs text-slate-400 italic">
              Les tarifs sont calculés selon les produits et le niveau de remplissage du groupage.
            </p>
          </div>
        </section>

        {/* FILTERS */}
        <section className="max-w-6xl mx-auto px-4 mb-6">
          <div className="bg-white rounded-xl border border-slate-200 p-4 flex flex-col md:flex-row gap-3">
            <div className="relative flex-1">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Rechercher un produit…"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-9"
                data-testid="deal-search-input"
              />
            </div>
            <select
              value={category}
              onChange={(e) => setCategory(e.target.value)}
              className="px-3 py-2 rounded-md border border-slate-200 text-sm"
              data-testid="deal-category-filter"
            >
              {categories.map(c => (
                <option key={c} value={c}>{c === 'all' ? 'Toutes catégories' : c}</option>
              ))}
            </select>
            <select
              value={sort}
              onChange={(e) => setSort(e.target.value)}
              className="px-3 py-2 rounded-md border border-slate-200 text-sm"
              data-testid="deal-sort-filter"
            >
              <option value="featured">À la une</option>
              <option value="popular">Plus populaires</option>
              <option value="price">Livraison la moins chère</option>
              <option value="unlocking">Objectif bientôt atteint</option>
            </select>
          </div>
        </section>

        {/* PRODUCTS GRID */}
        <section className="max-w-6xl mx-auto px-4">
          {filtered.length === 0 ? (
            <div className="text-center py-16 bg-white rounded-2xl border-2 border-dashed border-slate-200">
              <PackageIcon className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-600">Aucun produit ne correspond à votre recherche.</p>
            </div>
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
              {filtered.map((dp) => (
                <ProductCard key={dp.id} dp={dp} dealSlug={deal.slug} />
              ))}
            </div>
          )}
        </section>
      </main>
      <Footer />
    </>
  );
}

function Stat({ label, value, highlight }) {
  return (
    <div className={`rounded-lg px-3 py-2 ${highlight ? 'bg-green-500/20 border border-green-400/40' : 'bg-white/10'}`}>
      <div className="text-2xl font-black">{value ?? 0}</div>
      <div className="text-xs text-slate-300">{label}</div>
    </div>
  );
}

function ProductCard({ dp, dealSlug }) {
  const p = dp.product;
  const next = dp.next_tier;
  const productUrl = `${window.location.origin}/deals/${dealSlug}/p/${p?.slug}`;
  return (
    <div className="group bg-white rounded-xl border border-slate-200 overflow-hidden hover:border-amber-400 hover:shadow-lg transition-all relative" data-testid={`product-card-${p?.slug}`}>
      <div className="absolute top-2 right-2 z-10">
        <ShareButton
          variant="icon"
          url={productUrl}
          title={`${p?.brand ? p.brand + ' — ' : ''}${p?.title}`}
          message={`🔥 Deal ISM Parcel : ${p?.brand ? p.brand + ' ' : ''}${p?.title} à ${p?.merchant_price?.toFixed(2)}€ + ${dp.current_price?.toFixed(2)}€ livraison groupée. 👉 ${productUrl}`}
          testId={`card-share-${p?.slug}`}
        />
      </div>
      <Link to={`/deals/${dealSlug}/p/${p?.slug}`} className="block">
        <div className="relative aspect-square bg-slate-100 overflow-hidden">
          {p?.photos?.[0] ? (
            <img src={p.photos[0]} alt={p.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform" loading="lazy" />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-slate-300">
              <PackageIcon className="w-16 h-16" />
            </div>
          )}
          {dp.featured && (
            <Badge className="absolute top-2 left-2 bg-amber-500 hover:bg-amber-600">⭐ À la une</Badge>
          )}
        </div>
        <div className="p-4">
          {p?.brand && <div className="text-xs text-slate-500 uppercase tracking-wider font-semibold">{p.brand}</div>}
          <h3 className="font-bold text-slate-900 line-clamp-2 mb-2">{p?.title}</h3>
          {p?.merchant_price && (
            <div className="text-xs text-slate-500 mb-2">Prix marchand : <span className="font-semibold">{p.merchant_price.toFixed(2)} €</span></div>
          )}
          <div className="flex items-baseline gap-1 mb-3">
            <span className="text-xs text-slate-500">Livraison</span>
            <span className="text-2xl font-black text-amber-700">{dp.current_price?.toFixed(2)} €</span>
          </div>
          <div className="flex items-center justify-between text-xs text-slate-500 mb-3">
            <span>👀 {dp.interested_count || 0}</span>
            <span className="text-green-700 font-semibold">📦 {dp.received_orders_count || 0} reçues</span>
          </div>
          {/* Minimum viability badge (visible only if threshold set) */}
          {dp.min_orders_to_ship > 0 && !dp.is_viable && (
            <div className="bg-orange-50 border border-orange-200 rounded-lg px-2 py-1.5 text-xs text-orange-800 mb-2 flex items-center gap-1">
              <span>⏳</span>
              <span>Objectif : {dp.received_orders_count}/{dp.min_orders_to_ship} commandes</span>
            </div>
          )}
          {next && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg px-3 py-2 text-xs text-amber-900">
              <TrendingDown className="inline w-3 h-3 mr-1" />
              Encore <strong>{dp.orders_to_next_tier}</strong> commande(s) pour passer à <strong>{next.price.toFixed(2)} €</strong>
            </div>
          )}
        </div>
      </Link>
    </div>
  );
}
